import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, ChevronDown, ChevronUp, X, Pencil, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { licensingApi } from '@/api/licensing'
import { subscribersApi } from '@/api/subscribers'
import {
  LIMIT_KEYS, LIMIT_LABELS, WHITE_LABEL_LEVELS, WHITE_LABEL_LABELS,
  type LimitKey, type StandardLimits, type WhiteLabelLevel,
} from '@/types/licensing'
import { formatNIS } from '@/utils/currency'
import type { SystemLicensePlan } from '@/types'

function formatMoney(agorot: number): string {
  const nis = agorot / 100
  const formatted = Number.isInteger(nis) ? String(nis) : nis.toFixed(2)
  return `₪${formatted}`
}

const SYSTEM_COLORS: Record<string, string> = {
  jumpstart: '#16a34a',
  dugri: '#7c3aed',
  spaceviz: '#2563eb',
  milonyiftach: '#f59e0b',
  clientportal: '#8b5cf6',
  whatsapp: '#10b981',
}

const STATUS_COLORS: Record<string, string> = {
  active: '#4ade80',
  trial: '#fb923c',
  suspended: '#ef4444',
  cancelled: '#6b7280',
}

function getColor(code: string) {
  return SYSTEM_COLORS[code] ?? '#6366f1'
}

// ─── Limit helpers ─────────────────────────────────────────────────────────────

type LimitInput = { value: string; unlimited: boolean }
type LimitInputState = Record<LimitKey, LimitInput>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isLimitKey(key: string): key is LimitKey {
  return LIMIT_KEYS.includes(key as LimitKey)
}

function createLimitInputState(limits: Record<string, unknown> | null | undefined): LimitInputState {
  const source = isRecord(limits) ? limits : {}
  return LIMIT_KEYS.reduce((acc, key) => {
    const rawValue = source[key]
    acc[key] = {
      value: typeof rawValue === 'number' && rawValue >= 0 ? String(rawValue) : '',
      unlimited: rawValue === -1 || rawValue === null,
    }
    return acc
  }, {} as LimitInputState)
}

function limitInputsToStandardLimits(inputs: LimitInputState): StandardLimits {
  return LIMIT_KEYS.reduce((acc, key) => {
    const input = inputs[key]
    if (input.unlimited) {
      acc[key] = -1
      return acc
    }
    if (input.value.trim() !== '') {
      acc[key] = Number(input.value)
    }
    return acc
  }, {} as StandardLimits)
}

function extractUnknownLimits(limits: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(limits).filter(([key]) => !isLimitKey(key)))
}

function parseJsonValue(raw: string): unknown {
  return JSON.parse(raw)
}

function parseJsonObject(raw: string): Record<string, unknown> {
  const parsed = parseJsonValue(raw)
  if (!isRecord(parsed)) {
    throw new Error('JSON must be an object')
  }
  return parsed
}

function buildLimitsObject(inputs: LimitInputState, rawLimitsText: string): Record<string, unknown> {
  const parsed = parseJsonObject(rawLimitsText)
  return {
    ...extractUnknownLimits(parsed),
    ...limitInputsToStandardLimits(inputs),
  }
}

function syncLimitsText(inputs: LimitInputState, currentRawText: string): string {
  return JSON.stringify(buildLimitsObject(inputs, currentRawText), null, 2)
}

function formatLimitValue(value: any): string {
  if (value === -1 || value === null || value === undefined) return 'Unlimited'
  if (typeof value === 'number') {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`
    return String(value)
  }
  return String(value)
}

interface StandardLimitsEditorProps {
  inputs: LimitInputState
  onChange: (next: LimitInputState) => void
}

function StandardLimitsEditor({ inputs, onChange }: StandardLimitsEditorProps) {
  const update = (key: LimitKey, patch: Partial<LimitInput>) => {
    onChange({
      ...inputs,
      [key]: {
        ...inputs[key],
        ...patch,
      },
    })
  }

  return (
    <div className="space-y-3">
      {LIMIT_KEYS.map(key => {
        const meta = LIMIT_LABELS[key]
        const input = inputs[key]

        return (
          <div key={key} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_140px_auto] sm:items-center">
            <label className="text-sm text-white">
              {meta.label}
              {meta.unit && <span className="ml-1 text-xs text-gray-500">({meta.unit})</span>}
            </label>
            <input
              value={input.unlimited ? '' : input.value}
              onChange={e => update(key, { value: e.target.value })}
              disabled={input.unlimited}
              type="number"
              min="0"
              step="1"
              placeholder="0"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none font-mono"
              style={{
                background: input.unlimited ? '#1a1d27' : '#252836',
                border: '1px solid #2d3148',
                color: input.unlimited ? '#6b7280' : 'white',
              }}
            />
            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={input.unlimited}
                onChange={e =>
                  update(key, {
                    unlimited: e.target.checked,
                    value: e.target.checked ? '' : input.value,
                  })}
                className="rounded border-gray-600"
              />
              Unlimited
            </label>
          </div>
        )
      })}
    </div>
  )
}

interface WhiteLabelSelectorProps {
  value: WhiteLabelLevel
  onChange: (next: WhiteLabelLevel) => void
}

function WhiteLabelSelector({ value, onChange }: WhiteLabelSelectorProps) {
  return (
    <div className="flex gap-2">
      {WHITE_LABEL_LEVELS.map(level => {
        const active = value === level
        return (
          <button
            key={level}
            type="button"
            onClick={() => onChange(level)}
            className="flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
            style={{
              background: active ? '#6366f1' : '#252836',
              border: `1px solid ${active ? '#6366f1' : '#2d3148'}`,
              color: active ? 'white' : '#9ca3af',
            }}
          >
            {WHITE_LABEL_LABELS[level]}
          </button>
        )
      })}
    </div>
  )
}

interface AddPlanModalProps {
  systemCode: string
  onClose: () => void
  onSaved: () => void
}

function AddPlanModal({ systemCode, onClose, onSaved }: AddPlanModalProps) {
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    price_monthly_nis: '',
    price_yearly_nis: '0',
    features_raw: '{}',
    limits_raw: '{}',
  })
  const [limitInputs, setLimitInputs] = useState<LimitInputState>(createLimitInputState({}))
  const [whiteLabelLevel, setWhiteLabelLevel] = useState<WhiteLabelLevel>('none')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const qc = useQueryClient()

  const createMutation = useMutation({
    mutationFn: licensingApi.createPlan,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licensing-plans', systemCode] })
      qc.invalidateQueries({ queryKey: ['licensing-all-plans'] })
      toast.success('Plan created')
      onSaved()
    },
    onError: (e: any) => toast.error(e.message ?? 'Failed to create plan'),
  })

  const updateLimitInputs = (next: LimitInputState) => {
    setLimitInputs(next)
    setForm(prev => {
      try {
        return { ...prev, limits_raw: syncLimitsText(next, prev.limits_raw) }
      } catch {
        return prev
      }
    })
    setErrors(prev => ({ ...prev, limits_raw: '' }))
  }

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!form.code.trim()) errs.code = 'Required'
    if (!form.name.trim()) errs.name = 'Required'
    if (!form.price_monthly_nis || isNaN(Number(form.price_monthly_nis))) errs.price_monthly_nis = 'Valid number required'
    try { parseJsonValue(form.features_raw) } catch { errs.features_raw = 'Invalid JSON' }
    try { parseJsonObject(form.limits_raw) } catch { errs.limits_raw = 'Invalid JSON object' }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    createMutation.mutate({
      system_code: systemCode,
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_monthly_agorot: Math.round(Number(form.price_monthly_nis) * 100),
      price_yearly_agorot: Math.round(Number(form.price_yearly_nis || 0) * 100),
      features: parseJsonValue(form.features_raw) as Record<string, any>,
      limits: buildLimitsObject(limitInputs, form.limits_raw),
      white_label_level: whiteLabelLevel,
      is_active: true,
      sort_order: 0,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-xl p-6 space-y-4 overflow-y-auto max-h-[90vh]"
        style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Add Plan - {systemCode}</h2>
          <button onClick={onClose} style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-400">Code</label>
            <input
              value={form.code}
              onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
              placeholder="e.g. pro"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.code ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.code && <p className="text-xs text-red-400">{errors.code}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Pro"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.name ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Monthly Price (NIS)</label>
            <input
              value={form.price_monthly_nis}
              onChange={e => setForm(p => ({ ...p, price_monthly_nis: e.target.value }))}
              placeholder="0"
              type="number"
              min="0"
              step="1"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.price_monthly_nis ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.price_monthly_nis && <p className="text-xs text-red-400">{errors.price_monthly_nis}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Yearly Price (NIS) <span className="text-gray-600">optional</span></label>
            <input
              value={form.price_yearly_nis}
              onChange={e => setForm(p => ({ ...p, price_yearly_nis: e.target.value }))}
              placeholder="0"
              type="number"
              min="0"
              step="1"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-gray-400">Description <span className="text-gray-600">optional</span></label>
          <input
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs text-gray-400">
            Limits <span className="text-gray-600">- standard fields, -1 stored for unlimited</span>
          </label>
          <StandardLimitsEditor inputs={limitInputs} onChange={updateLimitInputs} />
        </div>

        <div className="space-y-2">
          <label className="text-xs text-gray-400">White label level</label>
          <WhiteLabelSelector value={whiteLabelLevel} onChange={setWhiteLabelLevel} />
        </div>

        <details className="rounded-lg" style={{ background: '#202434', border: '1px solid #2d3148' }}>
          <summary className="cursor-pointer px-3 py-2 text-sm text-white">Advanced - raw JSON</summary>
          <div className="p-3 pt-0 space-y-1">
            <textarea
              value={form.limits_raw}
              onChange={e => {
                const nextRaw = e.target.value
                setForm(prev => ({ ...prev, limits_raw: nextRaw }))
                setErrors(prev => ({ ...prev, limits_raw: '' }))
              }}
              rows={6}
              className="w-full px-3 py-2 rounded-lg text-xs text-white outline-none font-mono resize-none"
              style={{ background: '#252836', border: `1px solid ${errors.limits_raw ? '#ef4444' : '#2d3148'}` }}
            />
            <p className="text-[11px] text-gray-500">Unknown limit keys can be inspected or edited here. Standard keys above win on save.</p>
            {errors.limits_raw && <p className="text-xs text-red-400">{errors.limits_raw}</p>}
          </div>
        </details>

        <details className="rounded-lg" style={{ background: '#202434', border: '1px solid #2d3148' }}>
          <summary className="cursor-pointer px-3 py-2 text-sm text-white">Advanced - raw JSON</summary>
          <div className="p-3 pt-0 space-y-1">
            <textarea
              value={form.features_raw}
              onChange={e => setForm(p => ({ ...p, features_raw: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 rounded-lg text-xs text-white outline-none font-mono resize-none"
              style={{ background: '#252836', border: `1px solid ${errors.features_raw ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.features_raw && <p className="text-xs text-red-400">{errors.features_raw}</p>}
          </div>
        </details>

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: '#6366f1' }}
          >
            {createMutation.isPending ? 'Saving...' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  )
}


interface EditPlanModalProps {
  plan: SystemLicensePlan
  onClose: () => void
  onSaved: () => void
}

function EditPlanModal({ plan, onClose, onSaved }: EditPlanModalProps) {
  const initialLimitsRaw = JSON.stringify(plan.limits ?? {}, null, 2)
  const [form, setForm] = useState({
    name: plan.name,
    description: plan.description ?? '',
    price_monthly_nis: String(plan.price_monthly_agorot / 100),
    price_yearly_nis: String(plan.price_yearly_agorot / 100),
    features_raw: JSON.stringify(plan.features ?? {}, null, 2),
    limits_raw: initialLimitsRaw,
  })
  const [limitInputs, setLimitInputs] = useState<LimitInputState>(createLimitInputState(plan.limits))
  const [whiteLabelLevel, setWhiteLabelLevel] = useState<WhiteLabelLevel>(plan.white_label_level ?? 'none')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const qc = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<SystemLicensePlan>) => licensingApi.updatePlan(plan.id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licensing-plans', plan.system_code] })
      qc.invalidateQueries({ queryKey: ['licensing-all-plans'] })
      toast.success('Plan updated')
      onSaved()
    },
    onError: (e: any) => toast.error(e.message ?? 'Failed to update plan'),
  })

  const updateLimitInputs = (next: LimitInputState) => {
    setLimitInputs(next)
    setForm(prev => {
      try {
        return { ...prev, limits_raw: syncLimitsText(next, prev.limits_raw) }
      } catch {
        return prev
      }
    })
    setErrors(prev => ({ ...prev, limits_raw: '' }))
  }

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = 'Required'
    if (!form.price_monthly_nis || isNaN(Number(form.price_monthly_nis))) errs.price_monthly_nis = 'Valid number required'
    try { parseJsonValue(form.features_raw) } catch { errs.features_raw = 'Invalid JSON' }
    try { parseJsonObject(form.limits_raw) } catch { errs.limits_raw = 'Invalid JSON object' }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = () => {
    if (!validate()) return
    updateMutation.mutate({
      name: form.name.trim(),
      description: form.description.trim() || null,
      price_monthly_agorot: Math.round(Number(form.price_monthly_nis) * 100),
      price_yearly_agorot: Math.round(Number(form.price_yearly_nis || 0) * 100),
      features: parseJsonValue(form.features_raw) as Record<string, any>,
      limits: buildLimitsObject(limitInputs, form.limits_raw),
      white_label_level: whiteLabelLevel,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-xl p-6 space-y-4 overflow-y-auto max-h-[90vh]"
        style={{ background: '#1a1d27', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-white">Edit Plan - {plan.name}</h2>
            <code className="text-[11px]" style={{ color: '#6b7280' }}>{plan.system_code} / {plan.code}</code>
          </div>
          <button onClick={onClose} style={{ color: '#9ca3af' }}><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-400">Name</label>
            <input
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.name ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.name && <p className="text-xs text-red-400">{errors.name}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Description</label>
            <input
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Monthly Price (NIS)</label>
            <input
              value={form.price_monthly_nis}
              onChange={e => setForm(p => ({ ...p, price_monthly_nis: e.target.value }))}
              type="number"
              min="0"
              step="1"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: `1px solid ${errors.price_monthly_nis ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.price_monthly_nis && <p className="text-xs text-red-400">{errors.price_monthly_nis}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-400">Yearly Price (NIS)</label>
            <input
              value={form.price_yearly_nis}
              onChange={e => setForm(p => ({ ...p, price_yearly_nis: e.target.value }))}
              type="number"
              min="0"
              step="1"
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs text-gray-400">
            Limits <span className="text-gray-600">- standard fields, -1 stored for unlimited</span>
          </label>
          <StandardLimitsEditor inputs={limitInputs} onChange={updateLimitInputs} />
        </div>

        <div className="space-y-2">
          <label className="text-xs text-gray-400">White label level</label>
          <WhiteLabelSelector value={whiteLabelLevel} onChange={setWhiteLabelLevel} />
        </div>

        <details className="rounded-lg" style={{ background: '#202434', border: '1px solid #2d3148' }}>
          <summary className="cursor-pointer px-3 py-2 text-sm text-white">Advanced - raw JSON</summary>
          <div className="p-3 pt-0 space-y-1">
            <textarea
              value={form.limits_raw}
              onChange={e => {
                const nextRaw = e.target.value
                setForm(prev => ({ ...prev, limits_raw: nextRaw }))
                setErrors(prev => ({ ...prev, limits_raw: '' }))
              }}
              rows={6}
              className="w-full px-3 py-2 rounded-lg text-xs text-white outline-none font-mono resize-none"
              style={{ background: '#252836', border: `1px solid ${errors.limits_raw ? '#ef4444' : '#2d3148'}` }}
            />
            <p className="text-[11px] text-gray-500">Unknown limit keys can be inspected or edited here. Standard keys above win on save.</p>
            {errors.limits_raw && <p className="text-xs text-red-400">{errors.limits_raw}</p>}
          </div>
        </details>

        <details className="rounded-lg" style={{ background: '#202434', border: '1px solid #2d3148' }}>
          <summary className="cursor-pointer px-3 py-2 text-sm text-white">Advanced - raw JSON</summary>
          <div className="p-3 pt-0 space-y-1">
            <textarea
              value={form.features_raw}
              onChange={e => setForm(p => ({ ...p, features_raw: e.target.value }))}
              rows={4}
              className="w-full px-3 py-2 rounded-lg text-xs text-white outline-none font-mono resize-none"
              style={{ background: '#252836', border: `1px solid ${errors.features_raw ? '#ef4444' : '#2d3148'}` }}
            />
            {errors.features_raw && <p className="text-xs text-red-400">{errors.features_raw}</p>}
          </div>
        </details>

        <div className="flex justify-end gap-3 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white transition-colors"
            style={{ background: '#252836', border: '1px solid #2d3148' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
            style={{ background: '#6366f1' }}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Plans Table

interface PlansTableProps {
  systemCode: string
}

function PlansTable({ systemCode }: PlansTableProps) {
  const qc = useQueryClient()
  const [editingPlan, setEditingPlan] = useState<SystemLicensePlan | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['licensing-plans', systemCode, showInactive],
    queryFn: () => licensingApi.listPlans(systemCode, showInactive),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      licensingApi.updatePlan(id, { is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licensing-plans', systemCode] })
      qc.invalidateQueries({ queryKey: ['licensing-all-plans'] })
      toast.success('Plan updated')
    },
    onError: (e: any) => toast.error(e.message ?? 'Failed to update plan'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => licensingApi.deletePlan(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licensing-plans', systemCode] })
      qc.invalidateQueries({ queryKey: ['licensing-all-plans'] })
      toast.success('Plan deleted')
    },
    onError: (e: any) => toast.error(e.message ?? 'Failed to delete plan'),
  })

  const handleDelete = (plan: SystemLicensePlan) => {
    if (window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(plan.id)
    }
  }

  const inactiveToggle = (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: '#9ca3af' }}>
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="rounded border-gray-600"
        />
        Show inactive plans
      </label>
    </div>
  )

  if (isLoading) return (
    <>
      {inactiveToggle}
      <div className="flex items-center justify-center h-20">
        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2" style={{ borderColor: '#6366f1' }} />
      </div>
    </>
  )

  if (plans.length === 0) {
    if (systemCode === 'whatsapp') {
      return (
        <>
          {inactiveToggle}
          <div className="text-sm text-center py-8 space-y-3" style={{ color: '#6b7280' }}>
            <p className="text-white font-medium">No active plans — managed via JumpStart Chat</p>
            <p className="text-xs max-w-md mx-auto">
              Legacy Basic/Pro rows can be shown with &quot;Show inactive&quot; after they are retired in the database.
            </p>
            <Link
              to="/jumpstart-chat"
              className="inline-block text-indigo-400 hover:text-indigo-300 underline text-sm font-medium"
            >
              Open JumpStart Chat →
            </Link>
          </div>
        </>
      )
    }
    return (
      <>
        {inactiveToggle}
        <p className="text-sm text-center py-8" style={{ color: '#6b7280' }}>No plans configured</p>
      </>
    )
  }

  return (
    <>
      {inactiveToggle}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid #2d3148' }}>
              {['Plan', 'Monthly', 'Yearly', 'Limits', 'Status', 'Actions'].map(h => (
                <th key={h} className="text-left pb-2 pr-4 text-xs font-medium" style={{ color: '#6b7280' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => {
              const limits = plan.limits ?? {}
              const limitEntries = Object.entries(limits)
              return (
                <tr key={plan.id} style={{ borderBottom: '1px solid #2d314840' }}>
                  {/* Plan name + code */}
                  <td className="py-3 pr-4">
                    <div className="font-medium text-white">{plan.name}</div>
                    <code className="text-[11px]" style={{ color: '#6b7280' }}>{plan.code}</code>
                    {plan.description && (
                      <div className="text-[11px] mt-0.5" style={{ color: '#6b7280' }}>{plan.description}</div>
                    )}
                  </td>

                  {/* Monthly price */}
                  <td className="py-3 pr-4 font-mono text-xs" style={{ color: '#e2e8f0' }}>
                    {formatNIS(plan.price_monthly_agorot)}
                  </td>

                  {/* Yearly price */}
                  <td className="py-3 pr-4 font-mono text-xs" style={{ color: '#9ca3af' }}>
                    {plan.price_yearly_agorot > 0 ? formatNIS(plan.price_yearly_agorot) : <span style={{ color: '#4b5563' }}>—</span>}
                  </td>

                  {/* Limits — show key: value */}
                  <td className="py-3 pr-4">
                    {limitEntries.length === 0 ? (
                      <span className="text-[11px]" style={{ color: '#4b5563' }}>—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {limitEntries.map(([k, v]) => (
                          <span
                            key={k}
                            className="text-[10px] px-1.5 py-0.5 rounded-full"
                            style={{ background: '#f59e0b15', color: '#fbbf24', border: '1px solid #f59e0b20' }}
                          >
                            {k}: <strong>{formatLimitValue(v)}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>

                  {/* Status toggle */}
                  <td className="py-3 pr-4">
                    <button
                      onClick={() => toggleMutation.mutate({ id: plan.id, is_active: !plan.is_active })}
                      className="relative flex-shrink-0 w-9 h-5 rounded-full transition-all duration-200"
                      style={{ background: plan.is_active ? '#4ade8060' : '#2d3148' }}
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: plan.is_active ? 'translateX(16px)' : 'translateX(0)' }}
                      />
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingPlan(plan)}
                        title="Edit plan"
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: '#9ca3af' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#a5b4fc')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(plan)}
                        disabled={deleteMutation.isPending}
                        title="Delete plan"
                        className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
                        style={{ color: '#9ca3af' }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSaved={() => setEditingPlan(null)}
        />
      )}
    </>
  )
}

// ─── Subscriptions List ───────────────────────────────────────────────────────

interface SubsListProps {
  systemCode: string
}

function priceLabel(
  sub: { organization_id: string; effective_price_agorot?: number | null },
  billingByOrg: Map<string, { net_agorot: number; vat_agorot: number; gross_agorot: number }>,
): string {
  const computed = billingByOrg.get(sub.organization_id)
  if (computed && (computed.gross_agorot > 0 || computed.net_agorot > 0)) {
    return `${formatMoney(computed.net_agorot)} + ${formatMoney(computed.vat_agorot)} VAT = ${formatMoney(computed.gross_agorot)}`
  }
  if (typeof sub.effective_price_agorot === 'number') return formatNIS(sub.effective_price_agorot)
  return '—'
}

function SubsList({ systemCode }: SubsListProps) {
  const { data: subs = [], isLoading } = useQuery({
    queryKey: ['licensing-subs', systemCode],
    queryFn: () => licensingApi.listSubscriptions(systemCode),
  })
  const { data: subscribers } = useQuery({
    queryKey: ['subscribers-control-center'],
    queryFn: subscribersApi.list,
    enabled: systemCode === 'jumpstart',
    staleTime: 30_000,
  })
  const billingByOrg = new Map(
    (subscribers?.rows ?? []).map((r) => [r.organization_id, r]),
  )

  if (isLoading) return (
    <div className="flex items-center justify-center h-16">
      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2" style={{ borderColor: '#6366f1' }} />
    </div>
  )

  if (subs.length === 0) return (
    <p className="text-sm text-center py-6" style={{ color: '#6b7280' }}>No active subscriptions</p>
  )

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ borderBottom: '1px solid #2d3148' }}>
            {['Organization', 'Plan', 'Status', 'Billing', 'Price', 'Expires'].map(h => (
              <th key={h} className="text-left pb-2 pr-4 text-xs font-medium" style={{ color: '#6b7280' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subs.map(sub => (
            <tr key={sub.id} style={{ borderBottom: '1px solid #2d314840' }}>
              <td className="py-2.5 pr-4 font-medium text-white">{sub.organizations?.name ?? sub.organization_id}</td>
              <td className="py-2.5 pr-4">
                <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#2d3148', color: '#a5b4fc' }}>{sub.plan_code}</code>
              </td>
              <td className="py-2.5 pr-4">
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: `${STATUS_COLORS[sub.status] ?? '#6b7280'}20`, color: STATUS_COLORS[sub.status] ?? '#9ca3af' }}
                >
                  {sub.status}
                </span>
              </td>
              <td className="py-2.5 pr-4 text-xs" style={{ color: '#9ca3af' }}>{sub.billing_cycle ?? '—'}</td>
              <td className="py-2.5 pr-4 font-mono text-xs" style={{ color: '#9ca3af' }}>{priceLabel(sub, billingByOrg)}</td>
              <td className="py-2.5 text-xs" style={{ color: '#9ca3af' }}>
                {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LicensingPage() {
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null)
  const [showAddPlan, setShowAddPlan] = useState(false)

  const { data: systems = [], isLoading: loadingSystems } = useQuery({
    queryKey: ['licensing-systems'],
    queryFn: licensingApi.listSystems,
  })

  const { data: allPlans = [] } = useQuery({
    queryKey: ['licensing-all-plans'],
    queryFn: () => licensingApi.listAllPlans(false),
  })

  const { data: stats = {} } = useQuery({
    queryKey: ['licensing-stats'],
    queryFn: licensingApi.getSystemStats,
  })

  const { data: subscribers } = useQuery({
    queryKey: ['subscribers-control-center'],
    queryFn: subscribersApi.list,
    staleTime: 30_000,
  })

  const jumpstartComputedRevenue = (subscribers?.rows ?? [])
    .filter((r) => r.status === 'active')
    .reduce((sum, r) => sum + (r.net_agorot || 0), 0)

  const planCountBySystem = allPlans.reduce<Record<string, number>>((acc, p) => {
    acc[p.system_code] = (acc[p.system_code] ?? 0) + 1
    return acc
  }, {})

  const selectedSystemObj = systems.find(s => s.code === selectedSystem)

  if (loadingSystems) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: '#6366f1' }} />
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Licensing</h1>
        <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
          Manage license plans and subscriptions per system
        </p>
      </div>

      {/* System cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {systems.map(sys => {
          const color = getColor(sys.code)
          const sysStats = stats[sys.code] ?? { active_subs: 0, total_revenue: 0 }
          const revenue = sys.code === 'jumpstart' && jumpstartComputedRevenue > 0
            ? jumpstartComputedRevenue
            : sysStats.total_revenue
          const planCount = planCountBySystem[sys.code] ?? 0
          const isSelected = selectedSystem === sys.code
          const isClientPortal = sys.code === 'clientportal'

          return (
            <button
              key={sys.code}
              onClick={() => setSelectedSystem(isSelected ? null : sys.code)}
              className="rounded-xl p-5 text-left transition-all duration-150"
              style={{
                background: '#252836',
                border: `1px solid ${isSelected ? color : '#2d3148'}`,
                outline: 'none',
              }}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 text-lg font-bold"
                    style={{ background: `${color}20`, color, border: `1px solid ${color}50` }}
                  >
                    {sys.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white text-sm leading-tight">{sys.name}</h3>
                    <span className="text-[11px] font-mono" style={{ color: '#6b7280' }}>{sys.code}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {isClientPortal && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: '#16a34a20', color: '#4ade80' }}>
                      Included in JumpStart
                    </span>
                  )}
                  {isSelected ? <ChevronUp size={14} color="#9ca3af" /> : <ChevronDown size={14} color="#9ca3af" />}
                </div>
              </div>

              {sys.description && (
                <p className="text-xs mb-3" style={{ color: '#9ca3af' }}>{sys.description}</p>
              )}

              <div className="grid grid-cols-3 gap-2 pt-3" style={{ borderTop: '1px solid #2d3148' }}>
                <div>
                  <div className="text-xs" style={{ color: '#6b7280' }}>Plans</div>
                  <div className="text-sm font-semibold text-white">{planCount}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: '#6b7280' }}>Active subs</div>
                  <div className="text-sm font-semibold text-white">{sysStats.active_subs}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: '#6b7280' }}>Revenue/mo</div>
                  <div className="text-sm font-semibold text-white">{formatNIS(revenue)}</div>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Drill-down section */}
      {selectedSystem && selectedSystemObj && (
        <div
          className="rounded-xl p-6 space-y-6"
          style={{ background: '#252836', border: `1px solid ${getColor(selectedSystem)}40` }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold shrink-0"
                style={{ background: `${getColor(selectedSystem)}20`, color: getColor(selectedSystem) }}
              >
                {selectedSystemObj.name.charAt(0)}
              </div>
              <div>
                <h2 className="text-base font-semibold text-white">{selectedSystemObj.name} — Plans</h2>
                <p className="text-xs" style={{ color: '#9ca3af' }}>{selectedSystemObj.description}</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddPlan(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-white transition-colors"
              style={{ background: '#6366f1' }}
              onMouseEnter={e => e.currentTarget.style.background = '#4f46e5'}
              onMouseLeave={e => e.currentTarget.style.background = '#6366f1'}
            >
              <Plus size={13} /> Add Plan
            </button>
          </div>

          <PlansTable systemCode={selectedSystem} />

          <div>
            <h3 className="text-sm font-semibold text-white mb-3">Active Subscriptions</h3>
            <SubsList systemCode={selectedSystem} />
          </div>
        </div>
      )}

      {showAddPlan && selectedSystem && (
        <AddPlanModal
          systemCode={selectedSystem}
          onClose={() => setShowAddPlan(false)}
          onSaved={() => setShowAddPlan(false)}
        />
      )}
    </div>
  )
}
